/**
 * Venda na Rota — etapa 3 (Pagamento) e etapa 4 (Revisão e finalização, com a escolha do
 * documento: Pedido de Venda ou NF-e). Nada é gravado aqui: só "Finalizar" registra a venda.
 */
import {
  FISCAL_ENVIRONMENT_LABEL,
  formatDocument,
  formatMoney,
  formatQuantity,
  type Party,
  type PaymentMethod,
} from "@cafegestor/shared";
import type { ReactNode } from "react";
import { Alert, Field, Icon, type IconName, Input } from "@cafegestor/ui";
import { errorMessage } from "@cafegestor/client";
import { nfeMissing } from "./customer-lib";
import type { useRoutePreview } from "./rota-data";
import { lineTotal, type CartLine, type DocumentChoice } from "./rota-lib";

// ------------------------------------------------------------------ etapa 3
export function PaymentStep({
  methods,
  methodId,
  onMethod,
  payment,
  onPayment,
  total,
  customer,
}: {
  methods: PaymentMethod[];
  methodId: string;
  onMethod: (id: string) => void;
  payment: { e2e: string; auth: string };
  onPayment: (p: { e2e: string; auth: string }) => void;
  total: string | null;
  customer: Party | null;
}) {
  const method = methods.find((m) => m.id === methodId);
  const noCredit =
    method?.generatesReceivable === true &&
    customer !== null &&
    customer.creditLimit !== null &&
    Number(customer.creditLimit) === 0;
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between rounded-2xl bg-caramel-soft px-4 py-3">
        <span className="text-sm font-semibold text-primary">Total da venda</span>
        <span className="text-2xl font-bold tabular-nums text-primary">
          {total === null ? "—" : formatMoney(total)}
        </span>
      </div>
      {total === null && (
        <p className="text-[13px] text-muted-foreground">
          Sem conexão, o total final (preço da tabela) é calculado pelo servidor ao enviar.
        </p>
      )}
      <div role="radiogroup" aria-label="Forma de pagamento" className="grid gap-2">
        {methods.length === 0 && (
          <p className="text-sm text-muted-foreground">Nenhuma forma de pagamento cadastrada.</p>
        )}
        {methods.map((m) => {
          const on = m.id === methodId;
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onMethod(m.id)}
              className={`flex min-h-14 items-center gap-3 rounded-xl border-2 px-4 py-3 text-left ${
                on ? "border-primary bg-caramel-soft" : "border-border bg-card hover:bg-muted"
              }`}
            >
              <span
                aria-hidden
                className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  on ? "border-primary" : "border-input"
                }`}
              >
                {on && <span className="size-2.5 rounded-full bg-primary" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">{m.name}</span>
                <span className="block text-[13px] text-muted-foreground">
                  {m.generatesReceivable ? "A prazo (gera conta a receber)" : "À vista"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {noCredit && (
        <Alert tone="warn">
          Este cliente ainda não tem limite de crédito: a venda a prazo precisa de liberação do
          escritório. À vista, segue normalmente.
        </Alert>
      )}
      {method?.generatesReceivable && (
        <p className="text-[13px] text-muted-foreground">
          As parcelas seguem a forma de pagamento; o limite de crédito é conferido ao finalizar.
        </p>
      )}
      {method && (method.kind === "pix" || method.kind.startsWith("cartao")) && (
        <Field
          label={
            method.kind === "pix" ? "EndToEndId do PIX (opcional)" : "Autorização / NSU (opcional)"
          }
        >
          <Input
            autoComplete="off"
            value={method.kind === "pix" ? payment.e2e : payment.auth}
            onChange={(e) =>
              onPayment(
                method.kind === "pix"
                  ? { ...payment, e2e: e.target.value.trim() }
                  : { ...payment, auth: e.target.value.trim() },
              )
            }
          />
        </Field>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ etapa 4
function Section({
  icon,
  title,
  children,
}: {
  icon: IconName;
  title: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-2 flex items-center gap-2 font-semibold">
        <span
          aria-hidden
          className="flex size-8 items-center justify-center rounded-lg bg-caramel-soft text-accent"
        >
          <Icon name={icon} size={17} />
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

const DOC_CARDS: { key: DocumentChoice; icon: IconName; title: string; text: string }[] = [
  {
    key: "pedido",
    icon: "receipt",
    title: "Pedido de Venda",
    text: "Gerar e imprimir o pedido comercial para entregar ao cliente.",
  },
  {
    key: "nfe",
    icon: "fiscal",
    title: "Emitir Nota Fiscal Eletrônica",
    text: "Solicitar autorização da SEFAZ e imprimir o DANFE após a autorização.",
  },
];

export function ReviewStep({
  customer,
  lines,
  method,
  total,
  document,
  onDocument,
  online,
  preview,
  onEdit,
}: {
  customer: Party;
  lines: CartLine[];
  method: PaymentMethod | undefined;
  total: string | null;
  document: DocumentChoice;
  onDocument: (d: DocumentChoice) => void;
  online: boolean;
  preview: ReturnType<typeof useRoutePreview>;
  onEdit: (step: number) => void;
}) {
  const missing = nfeMissing(customer);
  const edit = (n: number, label: string) => (
    <button
      type="button"
      onClick={() => onEdit(n)}
      className="mt-2 text-sm font-semibold text-primary underline-offset-2 hover:underline"
    >
      {label}
    </button>
  );
  const p = preview.data;
  return (
    <div className="space-y-3" aria-label="Revisão da venda">
      <Section icon="user" title="Cliente">
        <p className="font-medium break-words">{customer.name}</p>
        {customer.document && (
          <p className="text-[13px] text-muted-foreground">{formatDocument(customer.document)}</p>
        )}
        {edit(0, "Trocar cliente")}
      </Section>
      <Section icon="cart" title={`Produtos (${lines.length})`}>
        <ul className="divide-y divide-border/70">
          {lines.map((l) => {
            const t = lineTotal(l);
            return (
              <li key={l.key} className="flex items-start justify-between gap-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block font-medium break-words">{l.productName}</span>
                  <span className="block text-[13px] text-muted-foreground tabular-nums">
                    {formatQuantity(l.quantity)} {l.unitCode}
                    {Number(l.discount) > 0 ? ` · desc. ${formatMoney(l.discount)}` : ""}
                  </span>
                </span>
                <span className="shrink-0 font-semibold tabular-nums">
                  {t === null ? "tabela" : formatMoney(t)}
                </span>
              </li>
            );
          })}
        </ul>
        {edit(1, "Editar produtos")}
      </Section>
      <Section icon="wallet" title="Pagamento">
        <p className="font-medium">{method?.name ?? "—"}</p>
        <p className="text-[13px] text-muted-foreground">
          {method?.generatesReceivable ? "A prazo" : "À vista"}
        </p>
        {edit(2, "Alterar pagamento")}
      </Section>
      <div className="flex items-baseline justify-between rounded-2xl bg-caramel-soft px-4 py-4">
        <span className="text-base font-semibold text-primary">Total geral</span>
        <span
          aria-label={`Total geral ${total === null ? "calculado ao enviar" : formatMoney(total)}`}
          className="text-[28px] font-bold leading-none tabular-nums text-primary"
        >
          {total === null ? "—" : formatMoney(total)}
        </span>
      </div>

      <fieldset className="min-w-0 space-y-2">
        <legend className="mb-1 text-base font-semibold">Documento para o cliente</legend>
        <div role="radiogroup" aria-label="Documento para o cliente" className="grid gap-2">
          {DOC_CARDS.map((c) => {
            const on = c.key === document;
            return (
              <button
                key={c.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onDocument(c.key)}
                className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left ${
                  on ? "border-primary bg-caramel-soft" : "border-border bg-card hover:bg-muted"
                }`}
              >
                <span
                  aria-hidden
                  className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${
                    on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Icon name={c.icon} size={22} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[16px] font-semibold">{c.title}</span>
                  <span className="block text-sm text-muted-foreground">{c.text}</span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {document === "pedido" && (
        <p className="rounded-xl bg-warning-soft px-3 py-2.5 text-[13px] text-warning-strong">
          O Pedido de Venda <strong>não tem valor fiscal e não substitui a nota fiscal</strong>.
          Quando a operação exigir, emita a NF-e pelo mesmo pedido (agora ou depois, em “Vendas
          desta carga”), sem repetir estoque nem financeiro.
        </p>
      )}
      {document === "nfe" && missing.length > 0 && (
        <Alert tone="warn">
          O cadastro do cliente não tem {missing.join("; ")}: a nota pode ser recusada na
          conferência.
        </Alert>
      )}
      {document === "nfe" && !online && (
        <p className="text-[13px] text-muted-foreground">
          Sem conexão: a venda fica guardada e a nota é pedida quando a venda for enviada.
        </p>
      )}
      {document === "nfe" && online && (
        <div role="status" aria-live="polite" className="text-sm">
          {preview.isFetching && !p && <p className="text-muted-foreground">Conferindo a nota…</p>}
          {preview.error !== null && (
            <Alert tone="error">
              Não foi possível conferir a nota: {errorMessage(preview.error)}
            </Alert>
          )}
          {p && p.calc.issues.length === 0 && (
            <p className="flex items-start gap-2 rounded-xl bg-success-soft px-3 py-2.5 text-success-strong">
              <Icon name="check" size={18} strokeWidth={2.4} className="mt-0.5 shrink-0" />
              <span>
                Nota conferida: {p.calc.model === "65" ? "NFC-e" : "NF-e"} (modelo{" "}
                {p.calc.model ?? "—"}) · {FISCAL_ENVIRONMENT_LABEL[p.environment]} · total{" "}
                {formatMoney(p.calc.totals.document)}.
              </span>
            </p>
          )}
          {p && p.calc.issues.length > 0 && (
            <Alert tone="error">
              <strong>A nota não pode ser emitida agora:</strong>
              <ul className="mt-1 list-disc pl-5">
                {p.calc.issues.map((i, n) => (
                  <li key={`${i.code}-${n}`}>{i.message}</li>
                ))}
              </ul>
              <span className="mt-1 block">
                Corrija o que falta ou escolha o Pedido de Venda (a NF-e pode ser emitida depois,
                pelo mesmo pedido).
              </span>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
